import { Check, Copy } from 'lucide-react';
import { useState } from 'react';

import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { Typography } from 'components/ui/typography';

import { Merchandise, MerchandiseVariant } from '../hooks/useProductImporter';

import { DownloadImagesButton } from './DownloadImagesButton';
import { MerchandiseCard } from './MerchandiseCard';
import { MerchandiseCarousel, MerchandiseCarouselLoading } from './MerchandiseCarousel';
import { Skeleton } from 'components/ui/skeleton';

interface MerchandiseDetailsProps {
  merchandise: Merchandise | null;
  variants?: MerchandiseVariant[];
  selectedVariantId?: string | null;
  onSelectVariant?: (variantId: string) => void;
}

export function MerchandiseDetails({
  merchandise,
  variants,
  selectedVariantId,
  onSelectVariant,
}: MerchandiseDetailsProps) {
  return (
    <MerchandiseCard>
      <div className="flex flex-1 flex-row gap-x-4">
        {merchandise ? (
          <MerchandiseCarousel images={merchandise.images} />
        ) : (
          <MerchandiseCarouselLoading />
        )}
        <div className="flex-1">
          {merchandise && (
            <div className="mb-2">
              <DownloadImagesButton
                images={merchandise.images}
                merchandiseTitle={merchandise.title}
              />
            </div>
          )}
          <Typography
            variant="h2"
            as="h2"
            className="group max-w-[90%] scroll-m-20 pb-2 transition-colors first:mt-0"
          >
            {merchandise ? (
              <>
                {merchandise.title} <CopyButton className="h-5 w-5" value={merchandise.title} />
              </>
            ) : (
              <Skeleton className="h-full w-full">&nbsp;</Skeleton>
            )}
          </Typography>
          <div className="flex flex-col gap-2">
            {merchandise && !merchandise.isAvailable && (
              <Badge className="w-fit" variant="destructive">
                Out of stock
              </Badge>
            )}
            {merchandise ? (
              merchandise.price && (
                <Field label="Price" size="lg">
                  {merchandise.price.displayValue}
                </Field>
              )
            ) : (
              <FieldLoading size="lg" />
            )}
            {merchandise &&
              variants &&
              variants.length > 1 &&
              selectedVariantId &&
              onSelectVariant && (
                <div className="max-w-xs space-y-1">
                  <Typography variant="label" as="p">
                    Variant
                  </Typography>
                  <Select value={selectedVariantId} onValueChange={onSelectVariant}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {variants.map((variant) => (
                        <SelectItem
                          key={variant.id}
                          value={variant.id}
                          disabled={!variant.isAvailable}
                        >
                          {variant.label}
                          {variant.isAvailable ? '' : ' (out of stock)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            {merchandise ? (
              merchandise.vendor && <Field label="Brand">{merchandise.vendor}</Field>
            ) : (
              <FieldLoading />
            )}
            {merchandise ? (
              merchandise.description && (
                <Field label="Description">{merchandise.description}</Field>
              )
            ) : (
              <FieldLoading long />
            )}
          </div>
        </div>
      </div>
    </MerchandiseCard>
  );
}

function CopyButton({ className, value }: { className?: string; value: string }) {
  const [didCopy, setDidCopy] = useState(false);

  const Icon = didCopy ? Check : Copy;
  function handleCopy() {
    setDidCopy(true);
    navigator.clipboard.writeText(value);
    setTimeout(() => setDidCopy(false), 2_000);
  }

  return (
    <Tooltip>
      <TooltipTrigger>
        <Icon
          className={cn(
            'hidden h-3 w-3 cursor-pointer text-muted-foreground hover:text-foreground group-hover:inline-block',
            className,
          )}
          role="button"
          onClick={handleCopy}
        />
      </TooltipTrigger>
      <TooltipContent>
        <Typography variant="small" as="p">
          {didCopy ? 'Copied!' : 'Click to copy'}
        </Typography>
      </TooltipContent>
    </Tooltip>
  );
}

interface MerchandiseFieldProps {
  children: string;
  label: string;
  size?: 'default' | 'lg';
}

function Field({ children, label, size = 'default' }: MerchandiseFieldProps) {
  return (
    <div className="max-w-lg space-y-1">
      <Typography variant="label" as="p" className="group flex items-center gap-1">
        {label} <CopyButton value={children} />
      </Typography>
      {size === 'lg' ? (
        <Typography variant="h3" as="p">
          {children}
        </Typography>
      ) : (
        <Typography variant="body" as="p">
          {children}
        </Typography>
      )}
    </div>
  );
}

function FieldLoading({ long, size }: { long?: boolean; size?: 'default' | 'lg' }) {
  const N_LINES = long ? 4 : 1;
  return (
    <div className="max-w-lg space-y-1">
      <Skeleton className="w-1/5">
        <Typography variant="label" as="p" className="group flex items-center gap-1">
          &nbsp;
        </Typography>
      </Skeleton>
      {Array.from({ length: N_LINES }).map((_, index) => (
        <Skeleton key={index} className={index + 1 === N_LINES ? 'w-1/2' : undefined}>
          {size === 'lg' ? (
            <Typography variant="h3" as="p">
              &nbsp;
            </Typography>
          ) : (
            <Typography variant="body" as="p">
              &nbsp;
            </Typography>
          )}
        </Skeleton>
      ))}
    </div>
  );
}
